import { useLiveQuery } from 'dexie-react-hooks'
import { useMemberIds } from '../../hooks/useMemberIds'
import { useEffect, useState } from 'react'
import { api } from '../../api/endpoints'
import { errorMessage, isManagedConflict } from '../../api/client'
import type { Bot, ManagedInfo, MemberRole, User } from '../../api/types'
import { Avatar } from '../../components/Avatar'
import { BotTag } from '../../components/BotTag'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { Dialog } from '../../components/Dialog'
import { Icon } from '../../components/Icon'
import type { MemberQuiet } from '../../api/types'
import { db, type MemberRow } from '../../db/db'
import { quietLabel } from './quiet'
import { usePopover } from '../../hooks/usePopover'
import { botAsUser, findBot, isBotMember, isBotUser, readsEverything, readsEverythingWarning } from '../../lib/bots'
import { userName } from '../../lib/channels'
import { pluralize } from '../../lib/format'
import { allowsManualMembers, isManagedMembership, isPlaceholderUser, managedInfo } from '../../lib/managed'
import { isRestricted } from '../../lib/posting'
import { everyMessageWarnings, useBots, useBotsStore } from '../../state/bots'
import { useConnection } from '../../state/connection'
import { useEphemeral } from '../../state/ephemeral'
import { toast } from '../../state/toasts'
import { syncAll } from '../../sync/syncEngine'
import { BotProfileButton } from '../bots/BotName'
import { useConversation } from '../conversation/ConversationContext'
import { toastChannelError } from './channelErrors'
import { UserPicker } from './UserPicker'

const NO_MEMBERS: MemberRow[] = []

interface BotsState {
  list: Bot[] | null
  error: string | null
}

export function MembersSection() {
  const { channel, users, me, canModerate } = useConversation()
  const loaded = useLiveQuery(() => db.members.where('channelId').equals(channel.id).toArray(), [channel.id])
  const rows = loaded ?? NO_MEMBERS
  const presence = useEphemeral((s) => s.online)
  const connected = useConnection((s) => s.hub === 'connected')
  // The server may not list the current user; this device being connected means online.
  const online = connected ? { ...presence, [me.id]: true as const } : presence
  const [adding, setAdding] = useState(false)
  const rowById = new Map(rows.map((r) => [r.userId, r]))
  const ids = [...new Set([...useMemberIds(channel), ...rows.map((r) => r.userId)])]
  // Bots are members, but not people: they get their own group and stay out of the count.
  const botIds = ids.filter((id) => isBotMember(id, rowById.get(id), users))
  const personIds = ids.filter((id) => !botIds.includes(id))
  const sorted = personIds.sort((a, b) => {
    if (a === me.id) return -1
    if (b === me.id) return 1
    const onlineDiff = Number(Boolean(online[b])) - Number(Boolean(online[a]))
    return onlineDiff || userName(users, a).localeCompare(userName(users, b))
  })
  const isChannel = channel.type === 'channel'
  const isGroup = channel.type === 'group_dm'
  // Channels and group chats can be managed.
  const managed = managedInfo(channel)
  // A managed_only conversation's membership is exactly the integration's list: nobody adds people by hand.
  const canAdd = channel.isMember && !channel.archivedAt && (isGroup || (isChannel && canModerate)) && allowsManualMembers(channel)
  // Channels: owners and admins choose the bots, on managed channels too (the integration only decides which people are members).
  // Group chats: any member adds bots with Add people and removes them here.
  const canAddBots = isChannel && canModerate && !channel.archivedAt
  const canRemoveBots = isChannel ? canAddBots : isGroup && channel.isMember
  const showBots = (isChannel && (botIds.length > 0 || canAddBots)) || (isGroup && botIds.length > 0)
  const bots = useBots(showBots || adding)
  const quietCount = personIds.filter((id) => id !== me.id && rowById.get(id)?.quiet).length

  return (
    <section className="drawer-section" aria-labelledby="members-heading">
      <div className="section-heading">
        <h3 id="members-heading">Members</h3>
        <span>
          {pluralize(personIds.length, 'member')}
          {quietCount ? ` · ${quietCount} muted` : ''}
        </span>
      </div>
      {canAdd ? (
        <button type="button" className="button small" style={{ marginBottom: 16 }} onClick={() => setAdding(true)}>
          <Icon name="plus" />
          {isGroup ? 'Add people or bots' : 'Add people'}
        </button>
      ) : null}
      {sorted.map((id) => {
        const row = rowById.get(id)
        return (
          <MemberItem
            key={id}
            userId={id}
            user={users.get(id)}
            role={row?.role ?? 'member'}
            managedBy={isManagedMembership(channel, row) ? managed : null}
            // Until the rows load, a managed channel can't tell managed members apart: offer no actions yet.
            actionsReady={Boolean(loaded) || !managed}
            online={Boolean(online[id])}
            quiet={rowById.get(id)?.quiet ?? null}
          />
        )
      })}
      {showBots ? <BotsGroup botIds={botIds} bots={bots} canAdd={canAddBots} canRemove={canRemoveBots} /> : null}
      {adding ? <AddMembersDialog existing={ids} managed={managed} onClose={() => setAdding(false)} /> : null}
    </section>
  )
}

interface MemberItemProps {
  userId: string
  user: User | undefined
  role: MemberRole
  /** Set for a managed member: the integration decides whether they stay and what role they have. */
  managedBy: ManagedInfo | null
  actionsReady: boolean
  online: boolean
  /** What this conversation will not do for them: everyone in it is shown this. */
  quiet: MemberQuiet
}

function MemberItem({ userId, user, role, managedBy, actionsReady, online, quiet }: MemberItemProps) {
  const { channel, me, users, canModerate } = useConversation()
  const menu = usePopover()
  const isMe = userId === me.id
  const placeholder = isPlaceholderUser(user)
  const manageable = channel.type === 'channel' && canModerate && channel.isMember && !isMe && !managedBy && actionsReady
  // Posters post in a restricted channel; elsewhere the role changes nothing, so it is only offered there.
  const offerPosting = role === 'poster' || (role === 'member' && isRestricted(channel))
  // Admins who are not owners make themselves posters to post in a restricted channel.
  const selfPosting =
    isMe && (me.role === 'admin' || me.role === 'sysadmin') && channel.type === 'channel' && channel.isMember && !managedBy && actionsReady && offerPosting
  const name = userName(users, userId)
  const quietText = quietLabel(quiet, 'short')

  const act = async (action: () => Promise<void>, success: string) => {
    menu.close()
    try {
      await action()
      await syncAll()
      toast(success)
    } catch (error) {
      toastChannelError('', error)
    }
  }

  return (
    <div className="member-row" ref={menu.ref}>
      <Avatar user={user} id={userId} online={online} />
      <span className="member-copy">
        <strong>{userName(users, userId)}</strong>
        <small>
          {placeholder ? 'Not signed in yet' : online ? 'Online' : 'Offline'}
          {role === 'owner' ? ' · Owner' : role === 'poster' ? ' · Poster' : ''}
          {user ? ` · @${user.username}` : ''}
          {quietText ? ` · ${quietText}` : ''}
        </small>
      </span>
      {/* Everyone in a conversation is shown who it will not notify. A bot has no notifications, so it never has one. */}
      {quietText ? (
        <span className="quiet-mark" role="img" aria-label={quietText} title={quietText}>
          <Icon name="mute" />
        </span>
      ) : null}
      {managedBy ? (
        <span className="member-tag" title={`Added by ${managedBy.integrationName}`}>
          Managed<span className="sr-only">, added by {managedBy.integrationName}</span>
        </span>
      ) : null}
      {isMe ? <span className="you-label">You</span> : null}
      {manageable || selfPosting ? (
        <>
          <button type="button" className="icon-button" aria-label={`Manage ${name}`} aria-expanded={menu.open} onClick={menu.toggle}>
            <Icon name="more" />
          </button>
          {menu.open && selfPosting ? (
            <div className="popover" role="menu">
              <PostingMenuItem role={role} onChange={(next) => void act(() => api.setMemberRole(channel.id, userId, next), next === 'poster' ? 'You can now post' : 'You can no longer post')} />
            </div>
          ) : menu.open ? (
            <div className="popover" role="menu">
              {role === 'owner' ? (
                <button type="button" role="menuitem" onClick={() => void act(() => api.setMemberRole(channel.id, userId, 'member'), 'Owner role removed')}>
                  <Icon name="user" />
                  Remove owner role
                </button>
              ) : (
                <button type="button" role="menuitem" onClick={() => void act(() => api.setMemberRole(channel.id, userId, 'owner'), 'Made an owner')}>
                  <Icon name="crown" />
                  Make owner
                </button>
              )}
              {offerPosting ? (
                <PostingMenuItem
                  role={role}
                  onChange={(next) => void act(() => api.setMemberRole(channel.id, userId, next), next === 'poster' ? `${name} can now post` : `${name} can no longer post`)}
                />
              ) : null}
              <button type="button" role="menuitem" className="danger" onClick={() => void act(() => api.removeMember(channel.id, userId), 'Member removed')}>
                <Icon name="leave" />
                Remove from channel
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  )
}

/** Toggles the poster role: "Allow to post" for a member, "Stop allowing to post" for a poster. */
function PostingMenuItem({ role, onChange }: { role: MemberRole; onChange: (next: MemberRole) => void }) {
  const poster = role === 'poster'
  return (
    <button type="button" role="menuitem" onClick={() => onChange(poster ? 'member' : 'poster')}>
      <Icon name="megaphone" />
      {poster ? 'Stop allowing to post' : 'Allow to post'}
    </button>
  )
}

function BotsGroup({ botIds, bots, canAdd, canRemove }: { botIds: string[]; bots: BotsState; canAdd: boolean; canRemove: boolean }) {
  const { channel, users } = useConversation()
  const [adding, setAdding] = useState(false)
  const isChannel = channel.type === 'channel'
  return (
    <div className="bots-group" role="group" aria-labelledby="bots-heading">
      <div className="section-heading">
        <h4 id="bots-heading">Bots</h4>
        <span>{pluralize(botIds.length, 'bot')}</span>
      </div>
      {canAdd ? (
        <button type="button" className="button small" style={{ marginBottom: 16 }} onClick={() => setAdding(true)}>
          <Icon name="plus" />
          Add bot
        </button>
      ) : null}
      {botIds.length === 0 && isChannel ? <p className="drawer-hint">No bots post in this channel.</p> : null}
      {botIds.map((id) => (
        <BotItem key={id} userId={id} user={users.get(id)} bot={findBot(bots.list, id)} canRemove={canRemove} />
      ))}
      {canAdd ? <ChannelIdForBots channelId={channel.id} /> : null}
      {adding ? <AddBotDialog installedUserIds={botIds} bots={bots} onClose={() => setAdding(false)} /> : null}
    </div>
  )
}

function BotItem({ userId, user, bot, canRemove }: { userId: string; user: User | undefined; bot: Bot | undefined; canRemove: boolean }) {
  const { channel, users } = useConversation()
  const menu = usePopover()
  const name = userName(users, userId, bot?.name ?? 'Bot')
  const isChannel = channel.type === 'channel'
  const details = [readsEverything(bot) ? 'Reads every message' : null, isChannel && bot?.postAnywhere ? 'Posts anywhere' : null].filter(Boolean)

  const remove = async () => {
    menu.close()
    try {
      // Channels: uninstall through the bot's integration when it is known; group chats remove the member.
      if (isChannel && bot) await api.uninstallBot(channel.id, bot.integrationId)
      else await api.removeMember(channel.id, userId)
      await syncAll()
      toast(
        isChannel && bot?.postAnywhere
          ? `${name} removed. It can post in any channel, so it rejoins the next time it posts here.`
          : `${name} removed from this ${isChannel ? 'channel' : 'conversation'}`,
      )
    } catch (error) {
      toastChannelError(`Couldn't remove ${name}.`, error)
    }
  }

  return (
    <div className="member-row bot-row" ref={menu.ref}>
      <BotProfileButton userId={userId} label={`About ${name}`} className="bot-row-profile">
        <Avatar user={user ?? (bot ? botAsUser(bot) : undefined)} id={userId} name={name} />
        <span className="member-copy">
          <strong>{name}</strong>
          <small>{details.length ? details.join(' · ') : isChannel ? 'Added to this channel' : 'In this conversation'}</small>
        </span>
      </BotProfileButton>
      <BotTag />
      {canRemove ? (
        <>
          <button type="button" className="icon-button" aria-label={`Manage ${name}`} aria-expanded={menu.open} onClick={menu.toggle}>
            <Icon name="more" />
          </button>
          {menu.open ? (
            <div className="popover" role="menu">
              <button type="button" role="menuitem" className="danger" onClick={() => void remove()}>
                <Icon name="leave" />
                {isChannel ? 'Remove from channel' : 'Remove from conversation'}
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  )
}

function ChannelIdForBots({ channelId }: { channelId: string }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(channelId)
      toast('Channel ID copied')
    } catch {
      toast("Couldn't copy. Select the ID and copy it by hand.", { tone: 'error' })
    }
  }
  return (
    <div className="channel-id">
      <span className="field-label" id="channel-id-label">
        Channel ID for bots
      </span>
      <div className="channel-id-value">
        <code aria-labelledby="channel-id-label">{channelId}</code>
        <button type="button" className="button small" aria-label="Copy channel ID" onClick={() => void copy()}>
          <Icon name="copy" />
          Copy
        </button>
      </div>
      <p className="field-help">Bots can only post in channels they have been added to, except bots allowed to post anywhere.</p>
    </div>
  )
}

function AddBotDialog({ installedUserIds, bots, onClose }: { installedUserIds: string[]; bots: BotsState; onClose: () => void }) {
  const { channel } = useConversation()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<Bot | null>(null)
  const available = (bots.list ?? []).filter((b) => !installedUserIds.includes(b.userId))

  // Names, webhooks and what bots receive can change: fetch them again when the picker opens.
  useEffect(() => {
    void useBotsStore.getState().load(true)
  }, [])

  const add = async (bot: Bot) => {
    setBusy(true)
    setError(null)
    try {
      await api.installBot(channel.id, bot.integrationId)
      await syncAll()
      toast(`${bot.name} added`)
      onClose()
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  let content: React.ReactNode
  if (bots.error && !bots.list) content = <p className="notice error">{bots.error}</p>
  else if (!bots.list) content = <p className="notice">Loading bots…</p>
  else if (available.length === 0)
    content = <p className="notice">{bots.list.length ? 'Every bot is already in this channel.' : 'There are no bots yet. A system admin sets them up under Bots.'}</p>
  else
    content = (
      <ul className="bot-pick-list">
        {available.map((bot) => (
          <li key={bot.integrationId} className="member-row">
            <Avatar user={botAsUser(bot)} id={bot.userId} name={bot.name} />
            <span className="member-copy">
              <strong>{bot.name}</strong>
              <small>
                {[
                  bot.description,
                  readsEverything(bot) ? 'Reads every message' : null,
                  bot.postAnywhere ? 'Can already post in any channel' : null,
                  bot.listed ? null : 'Unlisted',
                ]
                  .filter(Boolean)
                  .join(' · ') || 'Posts only where it has been added'}
              </small>
            </span>
            <button
              type="button"
              className="button small primary"
              disabled={busy}
              aria-label={`Add ${bot.name}`}
              onClick={() => (readsEverything(bot) ? setConfirm(bot) : void add(bot))}
            >
              Add
            </button>
          </li>
        ))}
      </ul>
    )

  return (
    <>
      <Dialog open onClose={onClose} title="Add a bot" titleId="add-bot-title" className="small-dialog" description="A bot posts updates from another system, and answers when it has a webhook.">
        <div className="dialog-body">
          {content}
          {error ? (
            <p className="field-error" role="alert">
              {error}
            </p>
          ) : null}
        </div>
        <div className="dialog-actions">
          <button type="button" className="button" onClick={onClose}>
            Close
          </button>
        </div>
      </Dialog>
      <ConfirmDialog open={Boolean(confirm)} title={`Add ${confirm?.name ?? 'bot'}?`} confirmLabel="Add bot" onClose={() => setConfirm(null)} onConfirm={() => (confirm ? add(confirm) : undefined)}>
        {confirm ? <p>{readsEverythingWarning(confirm)}</p> : null}
      </ConfirmDialog>
    </>
  )
}

/** "2 people", "1 bot", "2 people and 1 bot". */
function describePicked(people: number, bots: number): string {
  if (!bots) return pluralize(people, 'person', 'people')
  if (!people) return pluralize(bots, 'bot')
  return `${pluralize(people, 'person', 'people')} and ${pluralize(bots, 'bot')}`
}

function AddMembersDialog({ existing, managed, onClose }: { existing: string[]; managed: ManagedInfo | null; onClose: () => void }) {
  const { channel } = useConversation()
  const [picked, setPicked] = useState<User[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Set while asking about bots that would pass the whole group chat to their service.
  const [warnings, setWarnings] = useState<string[] | null>(null)
  const isGroup = channel.type === 'group_dm'
  const pickedBots = picked.filter(isBotUser)
  const pickedPeople = picked.length - pickedBots.length

  const start = async () => {
    setBusy(true)
    const found = await everyMessageWarnings(pickedBots)
    setBusy(false)
    if (found.length) setWarnings(found)
    else await add()
  }

  const add = async () => {
    setBusy(true)
    try {
      await api.addMembers(channel.id, picked.map((p) => p.id))
      await syncAll()
      toast(`Added ${describePicked(pickedPeople, pickedBots.length)}`)
      onClose()
    } catch (e) {
      if (isManagedConflict(e)) {
        toastChannelError('', e)
        onClose()
      } else {
        setError(errorMessage(e))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        title={isGroup ? 'Add people or bots' : 'Add people'}
        titleId="add-members-title"
        className="new-dialog"
        description={
          managed
            ? `This ${isGroup ? 'group chat' : 'channel'} is managed automatically. People you add here are manual members, so they can leave or be removed as normal.`
            : undefined
        }
      >
        <div className="dialog-body">
          <UserPicker selected={picked} onChange={setPicked} excludeIds={existing} includeBots={isGroup} label={isGroup ? 'Search staff and bots' : 'Search staff'} />
        </div>
        <div className="dialog-footer">
          <p role={error ? 'alert' : 'status'} className={error ? 'field-error' : undefined}>
            {error ?? (picked.length ? `${describePicked(pickedPeople, pickedBots.length)} selected` : isGroup ? 'Choose people or bots to add.' : 'Choose people to add.')}
          </p>
          <div className="dialog-actions">
            <button type="button" className="button" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="button primary" disabled={busy || !picked.length} onClick={() => void start()}>
              Add
            </button>
          </div>
        </div>
      </Dialog>
      <ConfirmDialog open={Boolean(warnings)} title="Add a bot that reads every message?" confirmLabel="Add" onClose={() => setWarnings(null)} onConfirm={add}>
        {warnings?.map((warning) => (
          <p key={warning}>{warning}</p>
        ))}
      </ConfirmDialog>
    </>
  )
}
